import { safeErrorDetails } from "../../utils/error-details.js";
import { QueryParams } from "../../types/query-params.js";
import { GsbSaveRequest, } from "../../types/requests.js";
import { GsbApiService } from "../../api/gsb-api.service.js";
import { logger } from "../../utils/logger.js";
import { isDefinitionSet } from "../../types/requests.js";
import { ProtocolGuards, } from "../../protocol.js";
import { BrowserRegistry } from "../../registry.js";
export function isMutableEntity(value) {
    return typeof value === "object" && value !== null;
}
export function hasStringId(value) {
    return (typeof value === "object" &&
        value !== null &&
        "id" in value &&
        typeof value.id === "string");
}
export class EntityService {
    apiService;
    useCache;
    useBulk;
    useAuth;
    apiBaseUrl;
    constructor(useCache = true, useBulk = true, useAuth = true, apiBaseUrl) {
        this.apiService = this.resolveApiService();
        this.useCache = useCache;
        this.useBulk = useBulk;
        this.useAuth = useAuth;
        this.apiBaseUrl = apiBaseUrl;
    }
    resolveApiService() {
        return GsbApiService.getInstance();
    }
    callApi(req, endPoint, token, tenantCode) {
        return this.apiService.callApi({
            useBulk: this.useBulk,
            ...req,
            ...(this.apiBaseUrl ? { apiBaseUrl: this.apiBaseUrl } : {}),
            ...(!this.useAuth ? { noAuth: true } : {}),
        }, endPoint, token, tenantCode);
    }
    reduceEntityDef(request, checkEntityDef = true) {
        if (checkEntityDef && !isDefinitionSet(request)) {
            throw new Error("Entity definition is required");
        }
        if (request.entityDef) {
            request.entityDef = {
                id: request.entityDef.id,
                name: request.entityDef.name,
            };
        }
    }
    async checkUnique(request, token, tenantCode) {
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: request,
        }, "/api/entity/checkUnique", token, tenantCode);
        return response;
    }
    async getById(definitionType, id, token, tenantCode) {
        const req = new QueryParams(definitionType);
        req.entityId = id;
        const result = await this.get(req, token, tenantCode);
        return result.entity ?? null;
    }
    async getCopy(req, token, tenantCode) {
        this.reduceEntityDef(req);
        if (!req.entityId) {
            throw new Error("Entity id is required");
        }
        req.disableTransaction = true;
        try {
            const response = await this.callApi({
                method: "POST",
                protocol: "https",
                hostName: "api",
                content: req,
                // Native bulk dispatch can acknowledge getCopy with no result.
                // Use its supported direct endpoint; never save an absent graph.
                useBulk: false,
            }, "/api/entity/getCopy", token, tenantCode);
            return response;
        }
        catch (error) {
            logger.debug(`Get entity failed for ${req.entDefName}`, safeErrorDetails(error));
            throw error;
        }
    }
    /**
     * Persist the native getCopy graph, never a projected list row.
     * Native-generated UUIDs own both identity and the disjoint Name namespace:
     * copy_<32 UUID hex digits>. No client-generated IDs or name-lookup/upsert guard.
     * A missing/reused native identity is an unsupported clone, not a save fallback.
     */
    async clone(request, token, tenantCode) {
        const sourceId = request.entityId;
        if (!sourceId?.trim())
            throw new Error("Entity id is required");
        const definition = request.entDefName ?? request.entDefId;
        if (!definition?.trim())
            throw new Error("Entity definition is required");
        const copyRequest = new QueryParams(definition);
        copyRequest.entityDef = request.entityDef;
        copyRequest.entityId = sourceId;
        const copy = await this.getCopy(copyRequest, token, tenantCode);
        if (!copy ||
            (copy.status !== undefined && copy.status >= 400) ||
            !ProtocolGuards.record(copy.entity)) {
            throw new Error("Native copy did not return an entity.");
        }
        const draft = copy.entity;
        const id = draft.id;
        if (typeof id !== "string" ||
            !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id) ||
            id.toLowerCase() === sourceId.toLowerCase()) {
            throw new Error("Native copy must supply a distinct UUID identity.");
        }
        let name;
        if (draft.name !== undefined) {
            if (typeof draft.name !== "string" || !draft.name.trim()) {
                throw new Error("Native copy returned an invalid Name.");
            }
            name = `copy_${id.replaceAll("-", "").toLowerCase()}`;
            if (name === draft.name) {
                throw new Error("Native copy did not supply a disjoint Name identity.");
            }
            draft.name = name;
        }
        const saveRequest = new GsbSaveRequest();
        saveRequest.entityDef = request.entityDef;
        saveRequest.entity = draft;
        const saved = await this.save(saveRequest, token, tenantCode);
        if (saved.id !== id) {
            throw new Error("Native copy save did not retain its new identity.");
        }
        const readRequest = new QueryParams(definition);
        readRequest.entityDef = request.entityDef;
        readRequest.entityId = id;
        const read = await this.get(readRequest, token, tenantCode);
        if (!read.entity ||
            read.entity.id !== id ||
            (name !== undefined &&
                (!ProtocolGuards.record(read.entity) || read.entity.name !== name))) {
            throw new Error("Native copy could not be verified after save.");
        }
        return read.entity;
    }
    async get(req, token, tenantCode) {
        if (!req.entityId) {
            throw new Error("Entity id is required");
        }
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        try {
            const response = await this.callApi({
                method: "POST",
                protocol: "https",
                hostName: "api",
                content: req,
            }, "/api/entity/get", token, tenantCode);
            return response;
        }
        catch (error) {
            logger.debug(`Get entity failed for ${req.entDefName}`, safeErrorDetails(error));
            throw error;
        }
    }
    async query(req, token, tenantCode) {
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        try {
            const response = await this.callApi({
                method: "POST",
                protocol: "https",
                hostName: "api",
                content: req,
            }, "/api/entity/query", token, tenantCode);
            const envelope = this.parseQueryEnvelope(response);
            if (!envelope.success) {
                throw new Error("Invalid entity query response.");
            }
            if (envelope.data.success === false ||
                (envelope.data.status !== undefined && envelope.data.status >= 400) ||
                envelope.data.error != null) {
                throw new Error("Entity query failed. Please retry or select another scope.");
            }
            if (!envelope.data.entities) {
                throw new Error("Entity query response is missing its rows.");
            }
            return response;
        }
        catch (error) {
            logger.debug(`Query failed for ${req.entDefName}`, safeErrorDetails(error));
            throw error;
        }
    }
    async saveEnt(entity, token, tenantCode) {
        if (!entity) {
            throw new Error("Entity is required");
        }
        const req = new GsbSaveRequest();
        if ("_entDefName" in entity) {
            req.entDefName = entity._entDefName;
        }
        req.entity = entity;
        const response = await this.save(req, token, tenantCode);
        if (response?.id) {
            entity.id = response.id;
        }
        return response;
    }
    async save(req, token, tenantCode) {
        if (!req.entity) {
            throw new Error("Entity is required");
        }
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/save", token, tenantCode);
        if (hasStringId(response) && isMutableEntity(req.entity)) {
            req.entity.id = response.id;
        }
        return response;
    }
    async updateQuery(req, token, tenantCode) {
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/updateQuery", token, tenantCode);
        return response;
    }
    async saveMulti(req, token, tenantCode) {
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/saveMulti", token, tenantCode);
        return response;
    }
    async executeBulk(bulkRequest, token, tenantCode) {
        try {
            const response = await this.apiService.callApi({
                method: "POST",
                protocol: "https",
                hostName: "api",
                content: bulkRequest,
                // A bulk request must never be folded into another bulk batch.
                useBulk: false,
            }, "/api/entity/bulk", token, tenantCode);
            return response;
        }
        catch (error) {
            logger.debug("Bulk operation failed", safeErrorDetails(error));
            throw error;
        }
    }
    async delete(req, token, tenantCode) {
        if (!req.entityId) {
            throw new Error("Entity id is required");
        }
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/delete", token, tenantCode);
        return response;
    }
    async deleteById(entDefName, id, token, tenantCode) {
        const req = new QueryParams(entDefName);
        req.entityId = id;
        if (!req.entityId) {
            throw new Error("Entity id is required");
        }
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/delete", token, tenantCode);
        return response;
    }
    async deleteEnt(entity, token, tenantCode) {
        const req = new QueryParams(entity._entDefName ?? "");
        req.entityId = entity.id;
        if (!req.entityId) {
            throw new Error("Entity id is required");
        }
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/delete", token, tenantCode);
        return response;
    }
    async deleteQuery(req, token, tenantCode) {
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/deleteQuery", token, tenantCode);
        return response;
    }
    queryParser;
    composeQueryValidation(parser) {
        this.queryParser = parser;
    }
    parseQueryEnvelope(value) {
        const guarded = ProtocolGuards.entityQuery(value);
        const core = this.queryParser?.safeParse(value);
        if (!guarded.success)
            return guarded;
        const composed = core ?? guarded;
        if (!composed.success)
            return composed;
        return (BrowserRegistry.lookup("validation")?.query.safeParse(value) ?? composed);
    }
}
//# sourceMappingURL=entity.service.js.map